import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { CustomerCreditTransaction, CustomerCreditsAdjustment, CustomerReferral, CustomerReferralProgram } from '@entities/customer'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage, runApiGet } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useCustomerCreditsStore } from '../../model/credits/store'

interface CreditsPageOptions {
  customerId: string | number
  page: number
  perPage: number
}

/** `GET …/referral-program` — the balance and the referral link. */
export const useCustomerReferralProgramApi = async (customerId: string | number): Promise<CustomerReferralProgram | null> => {
  const store = useCustomerCreditsStore()

  return runApiGet<CustomerReferralProgram>(useAdminRoutes('admin.customer.referral-program', { customer_id: customerId }), {
    setLoading: () => undefined,
    handle: (response) => {
      const program: CustomerReferralProgram = response.data
      store.setProgram(program)
      return program
    },
  })
}

/** `GET …/referral-program/referrals` — the customers this one referred, newest first. */
export const useCustomerReferralsApi = async (options: CreditsPageOptions): Promise<CustomerReferral[] | null> => {
  const store = useCustomerCreditsStore()

  return runApiGet<CustomerReferral[]>(useAdminRoutes('admin.customer.referrals', {
    customer_id: options.customerId,
    page: options.page,
    per_page: options.perPage,
  }), {
    setLoading: store.setReferralsLoading,
    handle: (response) => {
      const data = response.data as { referrals: CustomerReferral[], meta: Meta } | null
      store.setReferrals(data?.referrals ?? [])
      store.setReferralsMeta(data?.meta ?? null)
      return data?.referrals ?? []
    },
  })
}

/** The ledger is shown whole on one page, as on the legacy tab; 500 is the admin-api page-size cap. */
export const CREDITS_HISTORY_PER_PAGE = 500

/** `GET …/referral-program/credits-history` — the credit ledger, newest first. */
export const useCustomerCreditsHistoryApi = async (options: CreditsPageOptions): Promise<CustomerCreditTransaction[] | null> => {
  const store = useCustomerCreditsStore()

  return runApiGet<CustomerCreditTransaction[]>(useAdminRoutes('admin.customer.credits-history', {
    customer_id: options.customerId,
    page: options.page,
    per_page: options.perPage,
  }), {
    setLoading: store.setTransactionsLoading,
    handle: (response) => {
      const data = response.data as { transactions: CustomerCreditTransaction[], meta: Meta } | null
      store.setTransactions(data?.transactions ?? [])
      store.setTransactionsMeta(data?.meta ?? null)
      return data?.transactions ?? []
    },
  })
}

const ADJUST_ROUTES = {
  add: 'admin.customer.credits-add',
  remove: 'admin.customer.credits-remove',
} as const

const ADJUST_MESSAGES: Record<CustomerCreditsAdjustment, string> = {
  add: 'Credits were successfully added!',
  remove: 'Credits were successfully removed!',
}

interface CustomerCreditsAdjustOptions {
  customerId: string | number
  adjustment: CustomerCreditsAdjustment
  /** Positive integer cents, also for a removal. */
  amount: number
}

/** `POST …/credits/add|remove`. A removal above the balance answers 422 "Insufficient balance". */
export const useCustomerCreditsAdjustApi = async (options: CustomerCreditsAdjustOptions): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useCustomerCreditsStore()

  const url = useAdminRoutes(ADJUST_ROUTES[options.adjustment], { customer_id: options.customerId })

  store.setProcessing(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return false
    }

    const { data } = await $axios.post<ApiResponse>(url, { amount: options.amount })

    if (!data.success) {
      toast.error({ title: 'Error!', description: data.message || 'Something went wrong.' })
      return false
    }

    toast.success({ title: 'Success!', description: ADJUST_MESSAGES[options.adjustment] })

    return true
  } catch (err: unknown) {
    toast.error({ title: 'Error!', description: getAxiosErrorMessage(err) })
    return false
  } finally {
    store.setProcessing(false)
  }
}
